'use client';

import { useState, useEffect, useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useWallet } from './use-wallet';

const VAPID_PUBLIC_KEY = process.env.NEXT_PUBLIC_VAPID_PUBLIC_KEY;

function urlBase64ToUint8Array(base64String: string): Uint8Array<ArrayBuffer> {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = atob(base64);
  const buffer = new ArrayBuffer(rawData.length);
  const outputArray = new Uint8Array(buffer);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

async function buildAuthHeaders(
  getAccessToken: () => Promise<string | null>,
): Promise<HeadersInit> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  try {
    const token = await getAccessToken();
    if (token) headers.Authorization = `Bearer ${token}`;
  } catch {
    // Privy may be unavailable briefly
  }
  return headers;
}

type PermissionState = 'prompt' | 'granted' | 'denied' | 'unsupported';

/** Result of a subscribe attempt. Callers use `reason` to render targeted
 *  toasts instead of a single generic "failed" message. */
export type PushRequestResult =
  | { ok: true }
  | { ok: false; reason: 'unsupported' | 'no-wallet' | 'denied' | 'server' | 'error' };

/** Result of an unsubscribe attempt. `error` covers any thrown failure
 *  (service-worker gone, network off) so the caller can surface it. */
export type PushUnsubscribeResult =
  | { ok: true }
  | { ok: false; reason: 'no-wallet' | 'error' };

function detectIos(): boolean {
  if (typeof navigator === 'undefined') return false;
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
}

function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  return (
    (window.navigator as any).standalone === true ||
    window.matchMedia('(display-mode: standalone)').matches
  );
}

export function usePushNotifications() {
  const { address, getAccessToken, isAddressLinked } = useWallet();
  const queryClient = useQueryClient();
  const [permission, setPermission] = useState<PermissionState>('prompt');
  const [isLoading, setIsLoading] = useState(false);
  // Browser-side push subscription endpoint — read once via the
  // service-worker promise. Splitting this out from the server
  // verification means the per-render churn that used to re-fire
  // the subscribe-check fetch is now confined to a single
  // service-worker resolution.
  const [endpoint, setEndpoint] = useState<string | null>(null);

  const isIos = typeof window !== 'undefined' && detectIos();
  const isPwa = typeof window !== 'undefined' && isStandalone();

  const isSupported =
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    !!VAPID_PUBLIC_KEY;

  // iOS without PWA install: push not available but can be enabled by installing
  const isIosNonPwa = isIos && !isPwa && !isSupported;

  // Read the local PushSubscription endpoint once. This effect's
  // deps are intentionally minimal — the service worker's
  // subscription is a stable browser-level resource that doesn't
  // change with wallet switches, so we only re-resolve when push
  // becomes (un)supported.
  useEffect(() => {
    if (!isSupported) {
      setPermission('unsupported');
      setEndpoint(null);
      return;
    }
    setPermission(Notification.permission as PermissionState);

    let cancelled = false;
    navigator.serviceWorker.ready
      .then((registration) => registration.pushManager.getSubscription())
      .then((sub) => {
        if (cancelled) return;
        setEndpoint(sub?.endpoint ?? null);
      })
      .catch(() => {
        if (!cancelled) setEndpoint(null);
      });

    return () => {
      cancelled = true;
    };
  }, [isSupported]);

  // Server-side subscription status as a TanStack query. Previously
  // this lived in a `useEffect` whose deps included `getAccessToken`
  // — a Privy SDK reference that changes on every render, so the
  // effect re-fired (and re-fetched) constantly during wallet
  // switches and auth churn. The query caches by `(address, endpoint)`
  // tuple so consumers never duplicate the call regardless of how
  // many times the parent re-renders.
  //
  // `getAccessToken` is read INSIDE the queryFn, not via a stable
  // closure dep — TanStack only invalidates on key change, so the
  // changing reference can't retrigger the fetch.
  const subscriptionStatusQuery = useQuery<boolean>({
    queryKey: ['push-subscription-status', address?.toLowerCase() ?? null, endpoint],
    queryFn: async (): Promise<boolean> => {
      if (!address || !endpoint) return false;
      try {
        const headers = await buildAuthHeaders(getAccessToken);
        const res = await fetch(
          `/api/notifications/push/subscribe?address=${encodeURIComponent(address)}&endpoint=${encodeURIComponent(endpoint)}`,
          { headers, credentials: 'include' },
        );
        if (res.ok) {
          const data = await res.json();
          return data.subscribed === true;
        }
        if (res.status >= 500 || res.status === 429) {
          // Transient server/rate-limit — local PushSubscription still
          // exists, so prefer the optimistic answer over a flap.
          return true;
        }
        return false;
      } catch {
        // Network failure while verifying — prefer matching local
        // subscription state so the toggle reflects the browser's view.
        return true;
      }
    },
    // Same `requireAuth` gate as the rest of the notification stack.
    // Without it every wallet-switch re-fires this and 403s for
    // wallets the Privy session doesn't know about.
    enabled: isSupported && isAddressLinked && !!address && !!endpoint,
    staleTime: 5 * 60_000,
    // No `refetchInterval` — server state for "is this endpoint
    // registered for this wallet?" only changes on user action
    // (subscribe / unsubscribe), and both write paths invalidate
    // this query directly.
  });

  // Local subscription is the source of truth when push is
  // unsupported, the wallet isn't connected/linked, or no
  // service-worker subscription exists. Otherwise mirror the
  // server's view so the toggle reflects what the server actually
  // has stored for this (address, endpoint) tuple.
  const isSubscribed =
    isSupported && isAddressLinked && !!address && !!endpoint
      ? subscriptionStatusQuery.data === true
      : false;

  const requestPermission = useCallback(async (): Promise<PushRequestResult> => {
    if (!isSupported || !VAPID_PUBLIC_KEY) {
      return { ok: false, reason: 'unsupported' };
    }
    if (!address) {
      return { ok: false, reason: 'no-wallet' };
    }

    setIsLoading(true);
    try {
      const result = await Notification.requestPermission();
      setPermission(result as PermissionState);

      if (result !== 'granted') {
        return { ok: false, reason: 'denied' };
      }

      const registration = await navigator.serviceWorker.register('/sw.js');
      await navigator.serviceWorker.ready;

      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
      });

      const json = subscription.toJSON();
      const headers = await buildAuthHeaders(getAccessToken);

      const res = await fetch('/api/notifications/push/subscribe', {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify({
          address: address.toLowerCase(),
          subscription: {
            endpoint: json.endpoint,
            keys: {
              p256dh: json.keys?.p256dh,
              auth: json.keys?.auth,
            },
          },
        }),
      });

      // Reflect the new browser-side endpoint immediately so the
      // status query keys onto it without waiting for the next
      // service-worker resolution. Then mark this (address,
      // endpoint) tuple as subscribed in the cache so the toggle
      // flips before the verifying fetch returns.
      const newEndpoint = json.endpoint ?? null;
      if (newEndpoint) {
        setEndpoint(newEndpoint);
        queryClient.setQueryData<boolean>(
          ['push-subscription-status', address.toLowerCase(), newEndpoint],
          true,
        );
      }

      if (res.ok) {
        return { ok: true };
      }
      console.warn('[Push] Server did not persist subscription:', res.status);
      // Local PushSubscription still exists — keep master toggle on
      // so UI matches the browser. The optimistic cache write above
      // already does this; nothing more to do here.
      return { ok: false, reason: 'server' };
    } catch (error) {
      console.error('[Push] Failed to subscribe:', error);
      return { ok: false, reason: 'error' };
    } finally {
      setIsLoading(false);
    }
  }, [isSupported, address, getAccessToken, queryClient]);

  const unsubscribe = useCallback(async (): Promise<PushUnsubscribeResult> => {
    if (!address) return { ok: false, reason: 'no-wallet' };

    setIsLoading(true);
    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();

      if (subscription) {
        const endpoint = subscription.endpoint;
        const headers = await buildAuthHeaders(getAccessToken);

        // Drop only this wallet's row first. The server reports how many
        // other wallets on this device still depend on the same browser
        // endpoint — if any exist, we must NOT tear down the browser
        // subscription, otherwise sibling wallets would silently stop
        // receiving pushes (the bug this fix is targeting).
        let otherSubscribers = Number.POSITIVE_INFINITY;
        try {
          const res = await fetch(
            `/api/notifications/push/subscribe?address=${encodeURIComponent(address)}&endpoint=${encodeURIComponent(endpoint)}`,
            { method: 'DELETE', headers, credentials: 'include' },
          );
          if (res.ok) {
            const data = (await res.json()) as { otherSubscribers?: number };
            otherSubscribers =
              typeof data.otherSubscribers === 'number'
                ? data.otherSubscribers
                : Number.POSITIVE_INFINITY;
          }
        } catch {
          // Network failed — keep `otherSubscribers` at infinity so we err
          // on the side of preserving the browser subscription.
        }

        if (otherSubscribers === 0) {
          // Last wallet on this device just unsubscribed — safe to release
          // the browser-level push subscription. Reclaiming this also lets a
          // future re-subscribe mint a fresh endpoint instead of reviving
          // a dead one.
          await subscription.unsubscribe();
          // Browser endpoint is gone; clear local mirror so the
          // status query won't try to re-verify against a dead URL.
          setEndpoint(null);
        }

        // Optimistically flip THIS (address, endpoint) tuple in the
        // cache so the toggle switches off immediately even if the
        // browser endpoint is still live for sibling wallets.
        queryClient.setQueryData<boolean>(
          ['push-subscription-status', address.toLowerCase(), subscription.endpoint],
          false,
        );
      }

      return { ok: true };
    } catch (error) {
      console.error('[Push] Failed to unsubscribe:', error);
      return { ok: false, reason: 'error' };
    } finally {
      setIsLoading(false);
    }
  }, [address, getAccessToken, queryClient]);

  return {
    isSupported,
    isIosNonPwa,
    permission,
    isSubscribed,
    isLoading,
    requestPermission,
    unsubscribe,
  };
}
